/**
 * =============================================================================
 * GLUESTACK UI CONFIGURATION
 * =============================================================================
 *
 * This file defines the base theme configuration and component styles.
 *
 * CRITICAL: "CLEAN ROOM" PATTERN EXPORT
 * --------------------------------------
 * We export `componentsTheme` (the raw theme objects) so that themeGenerator.ts
 * can call createComponents() AFTER tenant tokens are known. This is essential
 * for the "clean room" initialization pattern.
 *
 * DO NOT remove the `export { componentsTheme }` line!
 *
 * WHY THIS MATTERS:
 * Gluestack-style v1 resolves tokens like $brandBase at createComponents() time.
 * If we only export `componentsConfig` (pre-processed), tenant colors won't work.
 * By exporting the raw themes, themeGenerator can re-process them with dynamic tokens.
 *
 * Related files:
 * - themeGenerator.ts (imports componentsTheme, calls createComponents)
 * - TenantSettingsStore.ts (gluestackConfig starts null for clean room)
 * - App.jsx (ThemeWrapper waits for tenant config)
 * =============================================================================
 */

import { createConfig, createComponents } from '@gluestack-style/react';
import { config as defaultConfig } from '@gluestack-ui/config';
import * as componentsTheme from './theme';
import { ThemeColors } from './themeGenerator';

// CRITICAL: Export raw themes for "clean room" re-creation in themeGenerator.ts
// DO NOT remove this export - it enables tenant-specific component theming!
export { componentsTheme };

export const componentsConfig = createComponents(componentsTheme);
type Components = typeof componentsConfig;

// 1. Define the RAW configuration object (no createConfig call yet)
export const configObject = {
  ...defaultConfig,
  tokens: {
    ...defaultConfig.tokens,
    colors: {
      ...defaultConfig.tokens.colors,
      // Neutral Grays (previously primary grays)
      neutral50: '#F9FAFB',
      neutral100: '#F2F4F7',
      neutral200: '#EAECF0',
      neutral300: '#D0D5DD',
      neutral400: '#98A2B3',
      neutral500: '#667085',
      neutral600: '#475467',
      neutral700: '#344054',
      neutral800: '#1D2939',
      neutral900: '#101828',

      backgroundLight100: '#F2F4F7',

      // Default Primary Palette (SNEXT Brand - Overridden by tenant)
      primaryColor: '#A9D14C',
      primary50: '#F7FBEA',
      primary100: '#EBF6D5',
      primary200: '#D7EDAB',
      primary300: '#C3E481',
      primary400: '#AFDB57',
      primary500: '#A9D14C',
      primary600: '#87AD3D',
      primary700: '#65822E',
      primary800: '#43571F',
      primary900: '#212C10',

      // Default Secondary Palette (Overridden by tenant)
      secondaryColor: '#5FB5D9',
      secondary50: '#F0F9FF',
      secondary100: '#E0F2FE',
      secondary200: '#BAE6FD',
      secondary300: '#7DD3FC',
      secondary400: '#38BDF8',
      secondary500: '#5FB5D9',
      secondary600: '#0284C7',
      secondary700: '#0369A1',
      secondary800: '#075985',
      secondary900: '#0C4A6E',

      // Default Accent Palette (Overridden by tenant)
      accentColor: '#4A4A4A',
      accent50: '#F2F2F2',
      accent100: '#E5E5E5',
      accent200: '#CCCCCC',
      accent300: '#B3B3B3',
      accent400: '#999999',
      accent500: '#4A4A4A',
      accent600: '#3D3D3D',
      accent700: '#2E2E2E',
      accent800: '#1F1F1F',
      accent900: '#101010',

      // Semantic Aliases: Typography
      textHeading: '$neutral900',
      textBody: '$neutral700',
      textSubtle: '$neutral500',
      textHint: '$neutral400',
      textBrand: '$primary800',
      textInverted: '$white',
      textDisabled: '$neutral300',

      // On-Color Tokens: Automatic foreground colors for colored surfaces
      // These ensure WCAG AA contrast compliance (4.5:1 automatically)
      // Note: Use hex strings as placeholders to help Gluestack's dynamic resolution
      textOnBrand: '#FFFFFF',
      textOnBrandMuted: '#EAECF0', // neutral200
      textOnBrandSubtle: '#101828',
      textOnSecondary: '#FFFFFF',
      textOnSecondarySubtle: '#101828',
      textOnSuccess: '#FFFFFF',
      textOnSuccessSubtle: '#101828',
      textOnError: '#FFFFFF',
      textOnErrorSubtle: '#101828',
      textOnWarning: '#FFFFFF',
      textOnWarningSubtle: '#101828',
      textOnInfo: '#FFFFFF',
      textOnInfoSubtle: '#101828',
      textOnDark: '#FFFFFF',

      // Semantic Aliases: Backgrounds
      // 2025-2026 Direction: White backgrounds, not grey canvas
      bgApp: '#FFFFFF',
      bgCard: '#FFFFFF',
      bgSubtle: '#F9FAFB',
      bgHover: '#F2F4F7',
      bgBrandSubtle: '#F7FBEA',
      bgSecondary: '#F0F9FF',
      bgOverlay: 'rgba(16, 24, 40, 0.05)',

      // Surface Tokens: Dark backgrounds with automatic text pairing
      bgBrand: '#65822E',
      bgDark: '#1D2939',

      // Semantic Aliases: Borders
      borderLight: '#EAECF0',
      borderMedium: '#D0D5DD',
      borderStrong: '#98A2B3',
      borderBrand: '#A9D14C',
      borderFocus: '#AFDB57',
      borderError: '#FCA5A5',
      borderSuccess: '#6EE7B7',

      // Semantic Aliases: Brand Actions
      brandBase: '#A9D14C',
      brandSubtle: '#D7EDAB',
      brandStrong: '#65822E',
      brandHover: '#87AD3D',
      brandActive: '#65822E',

      // Semantic Aliases: Interactive States
      actionPrimary: '#A9D14C',
      actionPrimaryHover: '#87AD3D',
      actionSecondary: '#5FB5D9',
      actionSecondaryHover: '#0284C7',
      actionDisabled: '#D0D5DD',

      // Semantic Aliases: Status (already harmonized by themeGenerator)
      statusSuccess: '#10B981',
      statusSuccessSubtle: '#F0FDF4',
      statusError: '#EF4444',
      statusErrorSubtle: '#FEF2F2',
      statusWarning: '#F59E0B',
      statusWarningSubtle: '#FFFBEB',
      statusInfo: '#3B82F6',
      statusInfoSubtle: '#EFF6FF',

      // Note: Module and Dimension colors (module0500, dim0500, etc.) are generated
      // dynamically by themeGenerator.ts based on tenant brand color
    },
    // Module and Dimension color palette names - actual colors generated by themeGenerator
    moduleColorPalette: [
      'lime',
      'teal',
      'orange',
      'red',
      'yellow',
      'purple',
      'cyan',
      'fuchsia',
      'violet',
      'blue',
      'emerald',
    ],
    dimensionColorPalette: ['dim0', 'dim1', 'dim2', 'dim3', 'dim4', 'dim5', 'dim6', 'dim7', 'dim8', 'dim9'],
    fonts: {
      ...defaultConfig.tokens.fonts,
      body: 'Inter-Regular',
      heading: 'Inter-SemiBold',
    },
    fontWeights: {
      normal: 400,
      semibold: 500,
      bold: 600,
    },
  },
  components: componentsConfig,
};

// 2. Export the PROCESSED config for initial boot
export const config = createConfig(configObject);

export type { UIConfig, UIComponents } from '@gluestack-ui/themed';

export interface IConfig {
  tokens: {
    colors: ThemeColors;
    moduleColorPalette: string[];
    dimensionColorPalette: string[];
    fonts: typeof defaultConfig.tokens.fonts;
    fontWeights: typeof defaultConfig.tokens.fontWeights;
  };
}

export interface IComponents extends Components {}
